import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './utStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const getStatusBadge = (status) => {
    let variant;
    switch (status) {
        case 'Pending':
            variant = 'primary';
            break;
        case 'Approved':
            variant = 'success';
            break;
        case 'Rejected':
            variant = 'danger';
            break;
        default:
            variant = 'secondary';
    }
    return <Badge bg={variant}>{status}</Badge>;
};

const Statuses = {
    Pending: 0,
    Approved: 1,
    Rejected: 2
};

const UndertimeList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [undertimeData, setUndertimeData] = useState([]);
    const [dropdownStates, setDropdownStates] = useState({});
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const searchParams = useSearchParams();
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const [statusFilters, setStatusFilters] = useState({
        Pending: true,
        Approved: true,
        Rejected: true,
    });

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const toggleDropdown = (itemId) => {
        setDropdownStates((prevState) => ({
            ...prevState,
            [itemId]: !prevState[itemId],
        }));
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setStatusFilters({
            ...statusFilters,
            [name]: checked,
        });
    };

    const fetchUndertimeData = async (searchTerm) => {
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');
        let apiUrlEndpoint = `${apiUrl}/Undertime?searchTerm=${searchTerm}&${statusQueryString}`;
        if (userAccess.hasApprover) {
            apiUrlEndpoint = `${apiUrl}/Undertime/ForApproval?searchTerm=${searchTerm}`;
        }
        try {
            const response = await fetch(apiUrlEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUndertimeData(data.Undertime);
            }
        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        }
        setIsLoading(false);
    };

    useEffect(() => {
        // Set a timeout to debounce the input change
        const timeoutId = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 1); // Delay the search to fetch data in 1 milliseconds

        // Clear the timeout on every input change
        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        // Fetch data with the debounced search term
        fetchUndertimeData(debouncedSearchInput);
    }, [debouncedSearchInput, statusFilters]);


    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = undertimeData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(undertimeData.length / itemsPerPage);

    const pageRange = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
    const endPage = Math.min(totalPages, startPage + pageRange - 1);

    return (
        <div>
            {userAccess.hasView ? (
                <>
                    <h3>Undertime Listing</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    {(userAccess.hasStandard || userAccess.hasAdmin) && (
                        <Link to='/undertime-create' className='btn btn-primary float-end mb-2'>Add undertime</Link>
                    )}

                    <div className='d-flex mb-2'>
                        <div className='custom-width'>
                            <input
                                type='text'
                                placeholder='Search..'
                                className='form-control'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        <div className="accordion ms-2" id="accordionFilter">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingFilter">
                                    <button
                                        className="accordion-button"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseFilter"
                                        aria-expanded="true"
                                        aria-controls="collapseFilter"
                                        style={{ height: '35px' }}
                                    >
                                        Status
                                    </button>
                                </h2>
                                <div
                                    id="collapseFilter"
                                    className="accordion-collapse collapse position-absolute"
                                    aria-labelledby="headingFilter"
                                    data-bs-parent="#accordionFilter"
                                >
                                    <div className="accordion-body">
                                        <div className="accordion" id="nestedAccordion">
                                            <div className="accordion-item border-2">
                                                <div
                                                    id="collapseStatusFilter"
                                                    className="accordion-collapse collapse show"
                                                    aria-labelledby="headingStatusFilter"
                                                    data-bs-parent="#nestedAccordion"
                                                >
                                                    <div className="accordion-body">
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id="pendingCheckbox"
                                                                name="Pending"
                                                                checked={statusFilters.Pending}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                            <label className="form-check-label" htmlFor="pendingCheckbox">
                                                                Pending
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id="approvedCheckbox"
                                                                name="Approved"
                                                                checked={statusFilters.Approved}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                            <label className="form-check-label" htmlFor="approvedCheckbox">
                                                                Approved
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id="rejectedCheckbox"
                                                                name="Rejected"
                                                                checked={statusFilters.Rejected}
                                                                onChange={handleCheckboxChange}
                                                            />
                                                            <label className="form-check-label" htmlFor="rejectedCheckbox">
                                                                Rejected
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <table className='custom-table'>
                        <thead>
                            <tr>
                                <th className='border-end-0'></th>
                                <th className='border-start-0 border-end-0'>Transaction ID</th>
                                <th className='border-start-0 border-end-0'>Transaction Date</th>
                                <th className='border-start-0 border-end-0'>Employee</th>
                                <th className='border-start-0 border-end-0'>Date Start</th>
                                <th className='border-start-0 border-end-0'>Date End</th>
                                <th className='border-start-0 border-end-0'>Duration</th>
                                <th className='border-start-0'>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="9">
                                        <div class="d-flex align-items-center">
                                            <div class="spinner-border me-2" aria-hidden="true"></div>
                                            <span role="status"> Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                            currentItems && currentItems.length > 0 ? (
                                currentItems.map((item) => (
                                    <React.Fragment key={item.ID}>
                                        <tr>
                                            <td className="text-sm-center remove-space border-end-0" style={{borderBottom: 'none'}}>
                                                <div className="dropdown">
                                                    <button
                                                        className="btn"
                                                        type="button"
                                                        id={`dropdownMenuButton${item.ID}`}
                                                        data-bs-toggle="dropdown"
                                                        data-bs-auto-close="outside"
                                                        aria-haspopup="true"
                                                        aria-expanded={dropdownStates[item.ID]}
                                                        onClick={() => toggleDropdown(item.ID)}
                                                    >
                                                        <i className="fas fa-ellipsis"></i>
                                                    </button>
                                                    <div
                                                        className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                                                        aria-labelledby={`dropdownMenuButton${item.ID}`}
                                                    >
                                                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                            <Link to={`/undertime-update/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Update
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasView || userAccess.hasAdmin) && (
                                                            <Link to={`/undertime-view/${item.ID}`} className="dropdown-item">
                                                                View
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                            <Link to={`/undertime-delete/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Delete
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                            <Link to={`/undertime-approve/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Approve
                                                            </Link>
                                                        )}
                                                        {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                            <Link to={`/undertime-reject/${item.ID}`}
                                                                className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                Reject
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className='border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.TransactionID}</td>
                                            <td className='border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.TransactionDate}</td>
                                            <td className='border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.Employee.Name}</td>
                                            <td className='border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.DateStart}</td>
                                            <td className='border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.DateEnd}</td>
                                            <td className='text-end border-start-0 border-end-0' style={{borderBottom: 'none'}}>{item.Duration}</td>
                                            <td className='border-start-0' style={{borderBottom: 'none'}}>{getStatusBadge(item.Status)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="9" style={{borderTop: 'none', paddingLeft: '65px', maxWidth: '50px'}}>
                                                <strong>Remarks:</strong> {item.Remarks}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9">No Record</td>
                                </tr>
                            )

                            )}
                        </tbody>
                    </table>
                    <nav aria-label="Page navigation">
                        <ul className="pagination mt-3">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <Link to={`/undertime-list?page=${currentPage - 1}`} className="page-link" onClick={() => handlePageChange(currentPage - 1)} aria-label="Previous">
                                    <span aria-hidden="true">&laquo;</span>
                                </Link>
                            </li>
                            {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
                                <li key={index} className={`page-item ${currentPage === startPage + index ? 'active' : ''}`}>
                                    <Link to={`/undertime-list?page=${startPage + index}`} className="page-link" onClick={() => handlePageChange(startPage + index)}>
                                        {startPage + index}
                                    </Link>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <Link to={`/undertime-list?page=${currentPage + 1}`} className="page-link" onClick={() => handlePageChange(currentPage + 1)} aria-label="Next">
                                    <span aria-hidden="true">&raquo;</span>
                                </Link>
                            </li>
                        </ul>
                    </nav>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    )
}

export default UndertimeList